// =====================================================
// 白板本地缓存（IndexedDB）
// 图片：fileId 为内容哈希，内容不可变，可永久缓存（LRU 淘汰）
// 项目快照：先展示缓存，再后台校验更新（stale-while-revalidate）
// 所有操作失败都静默降级，缓存不能影响正常加载
// =====================================================

import { supabase } from '@/config/supabase';
import type { WhiteboardProject } from '../types';

const DB_NAME = 'whiteboard-cache-v1';
const FILES = 'files';
const FILE_META = 'fileMeta';
const PROJECTS = 'projects';

const MAX_CACHE_BYTES = 500 * 1024 * 1024;
const PRUNE_TARGET_BYTES = 400 * 1024 * 1024;
const PRUNE_EVERY_N_WRITES = 40;

interface FileMeta {
  key: string;
  size: number;
  lastAccess: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;
let writesSincePrune = 0;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      db.createObjectStore(FILES);
      db.createObjectStore(FILE_META, { keyPath: 'key' });
      db.createObjectStore(PROJECTS, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

// ---------- 图片 ----------

export async function getCachedFile(key: string): Promise<Blob | null> {
  try {
    const db = await openDb();
    const blob = await reqToPromise<Blob | undefined>(
      db.transaction(FILES).objectStore(FILES).get(key),
    );
    if (!blob) return null;
    // 更新访问时间供 LRU 使用，失败无所谓
    const meta = db.transaction(FILE_META, 'readwrite').objectStore(FILE_META);
    meta.put({ key, size: blob.size, lastAccess: Date.now() } satisfies FileMeta);
    return blob;
  } catch {
    return null;
  }
}

export async function putCachedFile(key: string, blob: Blob): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction([FILES, FILE_META], 'readwrite');
    tx.objectStore(FILES).put(blob, key);
    tx.objectStore(FILE_META).put({ key, size: blob.size, lastAccess: Date.now() } satisfies FileMeta);
    await txDone(tx);
    if (++writesSincePrune >= PRUNE_EVERY_N_WRITES) {
      writesSincePrune = 0;
      void pruneFiles();
    }
  } catch {
    // 配额不足等情况直接忽略
  }
}

async function pruneFiles(): Promise<void> {
  try {
    const db = await openDb();
    const metas = await reqToPromise<FileMeta[]>(
      db.transaction(FILE_META).objectStore(FILE_META).getAll(),
    );
    let total = metas.reduce((sum, m) => sum + m.size, 0);
    if (total <= MAX_CACHE_BYTES) return;

    metas.sort((a, b) => a.lastAccess - b.lastAccess);
    const tx = db.transaction([FILES, FILE_META], 'readwrite');
    for (const m of metas) {
      if (total <= PRUNE_TARGET_BYTES) break;
      tx.objectStore(FILES).delete(m.key);
      tx.objectStore(FILE_META).delete(m.key);
      total -= m.size;
    }
    await txDone(tx);
  } catch {
    // 忽略
  }
}

// ---------- 项目快照 ----------

export async function getCachedProject(id: string): Promise<WhiteboardProject | null> {
  try {
    const db = await openDb();
    const row = await reqToPromise<{ id: string; project: WhiteboardProject } | undefined>(
      db.transaction(PROJECTS).objectStore(PROJECTS).get(id),
    );
    return row?.project ?? null;
  } catch {
    return null;
  }
}

export async function putCachedProject(project: WhiteboardProject): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(PROJECTS, 'readwrite');
    tx.objectStore(PROJECTS).put({ id: project.id, project, cachedAt: Date.now() });
    await txDone(tx);
  } catch {
    // 忽略
  }
}

export async function deleteCachedProject(id: string): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(PROJECTS, 'readwrite');
    tx.objectStore(PROJECTS).delete(id);
    await txDone(tx);
  } catch {
    // 忽略
  }
}

export async function clearWhiteboardCache(): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction([FILES, FILE_META, PROJECTS], 'readwrite');
    tx.objectStore(FILES).clear();
    tx.objectStore(FILE_META).clear();
    tx.objectStore(PROJECTS).clear();
    await txDone(tx);
  } catch {
    // 忽略
  }
}

// 请求持久化存储，避免系统在磁盘紧张时清掉缓存
void navigator.storage?.persist?.().catch(() => undefined);

// 退出登录时清空，避免下一个账号看到上一个账号的数据
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') void clearWhiteboardCache();
});
